// Help Center navigation model — mirrors the MAIN system navigation so users
// can find a help topic the same way they navigate the product. Groups appear
// in the same order as the left-nav (top-level items, then the Data / Workspace
// / Automation groups, then the Administration hub categories, then Developer).
//
// Within a group, section ids are listed in the order the matching features
// appear on their actual screens. Any help section NOT listed here still shows
// up — it falls into a trailing "More topics" group so nothing is ever lost.
import {
  Home, Search, Target, Gauge, MessageSquare, Database, Briefcase,
  ShieldCheck, Server, Settings, Code, Sparkles, List,
} from 'lucide-react';

// Groups mirror the main application sidebar (Home, ANALYZE, DECISIONS,
// MONITOR, GOVERNANCE, DATA, COLLABORATE, APPS, DEVELOPER) followed by the
// AI Coach / AI Platform topics and the Administration + Platform Console
// categories — so finding a help topic works the same way as navigating.
export const HELP_NAV_GROUPS = [
  { id: 'getting-started', label: 'Getting Started', icon: Home,
    sectionIds: ['home', 'quickstart', 'sample-datasets'] },

  { id: 'analyze', label: 'Analyze', icon: Search,
    sectionIds: ['explore-ai', 'ai-visual', 'scoped-measures', 'formulas', 'reports', 'report-builder', 'dashboard-view', 'ai-dashboard', 'ai-report', 'period-comparison', 'loss-triangle', 'investigations', 'executive-summaries', 'visual-library'] },

  { id: 'decisions', label: 'Decisions', icon: Target,
    sectionIds: ['my-work', 'initiatives', 'objectives', 'results', 'retention', 'scenario-simulator', 'decision-policies', 'ai-insights', 'recommendation-rules', 'risk-signals'] },

  { id: 'monitor', label: 'Monitor', icon: Gauge,
    sectionIds: ['kpi-monitor', 'sentinels', 'sentinel-packs', 'incidents', 'drift-alerts', 'alerts', 'schedules', 'anomaly-scans'] },

  { id: 'governance', label: 'Governance', icon: ShieldCheck,
    sectionIds: ['governance', 'adoption-analytics', 'certification-queue', 'metric-registry', 'calculation-packs', 'data-quality', 'data-catalog', 'lineage', 'impact-analysis', 'ai-coverage', 'explore-eval', 'pii-compliance', 'governance-analytics', 'change-requests', 'access-control', 'promotion', 'coach-studio'] },

  { id: 'data', label: 'Data', icon: Database,
    sectionIds: ['data-sources', 'data-prep', 'data-prep-transforms', 'semantic-models', 'glossary', 'tableau-import'] },

  { id: 'collaborate', label: 'Collaborate', icon: MessageSquare,
    sectionIds: ['discussions', 'activity', 'messaging', 'presence-follow', 'co-editing', 'notifications'] },

  { id: 'apps', label: 'Apps', icon: Briefcase,
    sectionIds: ['apps-workspaces', 'marketplace'] },

  { id: 'developer', label: 'Developer', icon: Code,
    sectionIds: ['sdk-visuals', 'sdk-connectors', 'embed-manager', 'mgmt-api', 'embed-admin-panels', 'signed-webhooks', 'self-hosting'] },

  { id: 'ai-coach', label: 'AI Coach', icon: Sparkles,
    sectionIds: ['ai-coach', 'autonomous-coach', 'capability-studio'] },

  { id: 'ai-platform', label: 'AI Platform', icon: Sparkles,
    sectionIds: ['llm-providers', 'ai-agents', 'voice', 'ai-traces', 'ai-usage'] },

  { id: 'administration', label: 'Administration', icon: Settings,
    sectionIds: ['administration', 'users', 'tenant-admin', 'sso', 'audit-log', 'api-keys', 'settings', 'branding', 'version-control', 'inbound-emails', 'feedback-admin', 'doc-analytics', 'pyramid-mapping', 'workspace-flags'] },

  { id: 'platform-console', label: 'Platform Console', icon: Server,
    sectionIds: ['platform-console', 'admin-console', 'platform-health', 'deploy-status', 'system-updates', 'site-root', 'background-jobs', 'all-schedules', 'system-logs', 'tenant-lifecycle', 'signup-requests', 'signup-invites', 'trial-leads', 'revenue', 'billing', 'object-storage', 'delegated-admin', 'identity-bridge'] },
];

/**
 * Arrange the (possibly DB-sourced) help sections into ordered nav groups.
 * Returns [{ id, label, icon, items: Section[] }] with any unmapped section
 * appended to a trailing "More topics" group.
 */
export function buildHelpGroups(sections) {
  const byId = new Map((sections || []).map((s) => [s.id, s]));
  const used = new Set();
  const groups = [];

  for (const g of HELP_NAV_GROUPS) {
    const items = [];
    for (const sid of g.sectionIds) {
      const sec = byId.get(sid);
      if (sec) { items.push(sec); used.add(sid); }
    }
    if (items.length) groups.push({ id: g.id, label: g.label, icon: g.icon, items });
  }

  const leftovers = (sections || []).filter((s) => !used.has(s.id));
  if (leftovers.length) {
    groups.push({ id: 'more', label: 'More topics', icon: List, items: leftovers });
  }
  return groups;
}
